import { queryOptions, useQueries } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type {
  DocumentResponse,
  WellMapResponse,
  WellboreMapData,
} from 'types/api-responses';
import { getAfeTargetUwis } from './utils';

const QUERY_KEY = ['documents', 'overview'] as const;

const NO_WELLBORES: WellboreMapData[] = [];

type AfeWellbores = { wellbores: WellboreMapData[]; isSettled: boolean };

/**
 * The wellbores one well lookup draws: its drilled trajectories, or for a
 * permit / pre-drill well (no trajectory) its surface hole.
 * @param {WellMapResponse} response - The `queries/wells` response for one UWI
 * @return {WellboreMapData[]} The wellbores to plot
 */
const responseWellbores = (response: WellMapResponse): WellboreMapData[] =>
  response.results.flatMap((result) => {
    const wellBores = result.wellBores ?? [];
    if (wellBores.length > 0) return wellBores;
    return result.surfaceHole
      ? [
          {
            uwi14: result.uwi,
            uwi12: result.uwi,
            type: 'producing' as const,
            surfaceHole: result.surfaceHole,
          },
        ]
      : [];
  });

/**
 * Query options for the wellbore map geometry of one AFE well. `queries/wells`
 * only accepts a single `uwi` per query, so each UWI is its own query. A lookup
 * that fails stays failed rather than cached as empty, so the next visit tries
 * it again; the map draws the wells that did load.
 * @param {string} uwi - The UWI to look up
 * @return {object} The lookup's query options
 */
export function afeWellboreOptions(uwi: string) {
  return queryOptions({
    queryKey: [...QUERY_KEY, 'afe-wellbores', uwi],
    queryFn: async ({ signal }) => {
      const response = await api
        .post('queries/wells', {
          json: { uwi, includeDeviationSurvey2d: true },
          signal,
          // A slow well lookup still draws; these never had a timeout.
          timeout: false,
        })
        .json<WellMapResponse>();
      return responseWellbores(response);
    },
    // A well's geometry doesn't change while the page is open.
    staleTime: Infinity,
  });
}

/**
 * The wellbores of the lookups that loaded, and whether every lookup has
 * settled. Module-level so React Query keeps the same result while the lookups
 * are unchanged, which the map's memos rely on.
 * @param {Array} results - One result per well lookup
 * @return {AfeWellbores} The loaded wellbores and whether all lookups settled
 */
const loadedWellbores = (
  results: { data?: WellboreMapData[]; isPending: boolean }[]
): AfeWellbores => {
  const loaded = results.flatMap((result) => result.data ?? []);
  return {
    wellbores: loaded.length > 0 ? loaded : NO_WELLBORES,
    isSettled: results.every((result) => !result.isPending),
  };
};

/**
 * The wellbores of the wells matched to an AFE document's targets. Every
 * other document type gets an empty list without any request.
 * @param {DocumentResponse} document - The document whose AFE targets to map
 * @return {AfeWellbores} The loaded wellbores and whether all lookups settled
 */
export function useAfeWellbores(document: DocumentResponse): AfeWellbores {
  return useQueries({
    queries: getAfeTargetUwis(document).map((uwi) => afeWellboreOptions(uwi)),
    combine: loadedWellbores,
  });
}
